import { useEffect, useState } from 'react';
import {
  BoutonPrincipal,
  Carte,
  Chargement,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../components/ui/Primitives';
import { useToast } from '../components/ui/useToast';
import {
  useCriteresMatching,
  useModifierBareme,
  type CritereHorsScore,
  type EntreeBareme,
  type LigneCritere,
} from '../lib/useCriteresMatching';
import { ErreurApi } from '../lib/api';

function horodatage(valeur: string): string {
  return new Date(valeur).toLocaleString('fr-FR', { timeZone: 'Indian/Reunion' });
}

/**
 * Écran « Critères de matching » — responsable pédagogique et direction.
 *
 * Les poids se règlent ici plutôt que dans le code. La somme des critères
 * actifs doit faire cent : le score se lit comme un pourcentage partout, du
 * seuil de notification aux tranches de l'historique. Le total est vérifié à
 * l'écran, et l'API le refuse de toute façon.
 *
 * L'écran n'affiche que les critères qui rapportent réellement des points. Ce
 * qui entre dans le calcul sans être pondérable — antenne, places restantes,
 * RQTH — est listé à part, avec son rôle : un curseur sans effet sur un
 * score ferait croire à un arbitrage qui n'existe pas.
 */
export function ComCriteresMatchingPage(): JSX.Element {
  const { data, isLoading, error } = useCriteresMatching();
  const modifier = useModifierBareme();
  const toast = useToast();

  const [brouillon, setBrouillon] = useState<EntreeBareme | null>(null);

  useEffect(() => {
    if (!data) return;
    setBrouillon({
      criteres: data.criteres.map((critere) => ({
        code: critere.code,
        poids: critere.poids,
        actif: critere.actif,
      })),
      seuilNotification: data.seuilNotification,
      seuilProposition: data.seuilProposition,
      limitePropositions: data.limitePropositions,
      batchActif: data.batchActif,
    });
  }, [data]);

  if (error) {
    return (
      <div className="mx-auto max-w-[1100px] p-4 sm:p-6">
        <MessageErreur
          message={
            error instanceof ErreurApi ? error.message : 'Le barème n’a pas pu être chargé.'
          }
        />
      </div>
    );
  }
  if (isLoading || !data || !brouillon) {
    return (
      <div className="mx-auto max-w-[1100px] p-4 sm:p-6">
        <Chargement libelle="Chargement du barème…" />
      </div>
    );
  }

  const totalAttendu = data.totalAttendu;
  const total = brouillon.criteres
    .filter((critere) => critere.actif)
    .reduce((somme, critere) => somme + critere.poids, 0);
  const totalJuste = total === totalAttendu;
  const seuilsCoherents = brouillon.seuilProposition <= brouillon.seuilNotification;
  const enregistrable = totalJuste && seuilsCoherents && !modifier.isPending;

  const changerPoids = (code: string, poids: number): void => {
    setBrouillon((precedent) =>
      precedent === null
        ? precedent
        : {
            ...precedent,
            criteres: precedent.criteres.map((critere) =>
              critere.code === code ? { ...critere, poids } : critere,
            ),
          },
    );
  };

  const basculerActif = (code: string): void => {
    setBrouillon((precedent) =>
      precedent === null
        ? precedent
        : {
            ...precedent,
            criteres: precedent.criteres.map((critere) =>
              critere.code === code ? { ...critere, actif: !critere.actif } : critere,
            ),
          },
    );
  };

  const changerReglage = (champ: keyof EntreeBareme, valeur: number | boolean): void => {
    setBrouillon((precedent) =>
      precedent === null ? precedent : { ...precedent, [champ]: valeur },
    );
  };

  return (
    <div className="mx-auto max-w-[1100px] space-y-6 p-4 md:p-6">
      <EnteteEcran
        titre="Critères de matching"
        sousTitre="Pondération du score candidat / entreprise · responsable pédagogique et direction"
        actions={
          <BoutonPrincipal
            onClick={() =>
              modifier.mutate(brouillon, {
                onSuccess: () => toast('Barème enregistré · les prochains calculs l’appliquent'),
              })
            }
            disabled={!enregistrable}
          >
            Enregistrer le barème
          </BoutonPrincipal>
        }
      />

      {modifier.error instanceof ErreurApi && <MessageErreur message={modifier.error.message} />}

      <div
        className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl p-4 md:p-6 ${
          totalJuste ? 'bg-codialis-sand' : 'bg-red-50'
        }`}
      >
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
            Total des critères actifs
          </div>
          <div className="font-display text-3xl font-extrabold text-codialis-navy">
            {total}
            <span className="text-base text-codialis-gray"> / {totalAttendu}</span>
          </div>
        </div>
        <p className="max-w-md text-xs text-codialis-gray">
          {totalJuste
            ? 'Le score se lit comme un pourcentage : seuils, tranches de l’historique et affichage restent cohérents.'
            : `${total < totalAttendu ? `Il manque ${totalAttendu - total} point(s)` : `${total - totalAttendu} point(s) de trop`} pour atteindre ${totalAttendu}. L’enregistrement est bloqué tant que le total diffère.`}
        </p>
      </div>

      <Carte titre="Poids des critères" sansBordure>
        <div className="divide-y divide-gray-100">
          {data.criteres.map((critere) => (
            <ReglageCritere
              key={critere.code}
              critere={critere}
              poids={
                brouillon.criteres.find((ligne) => ligne.code === critere.code)?.poids ??
                critere.poids
              }
              actif={
                brouillon.criteres.find((ligne) => ligne.code === critere.code)?.actif ??
                critere.actif
              }
              onPoids={(poids) => changerPoids(critere.code, poids)}
              onBasculer={() => basculerActif(critere.code)}
            />
          ))}
        </div>
      </Carte>

      <Carte titre="Pris en compte sans être pondéré" sansBordure>
        <p className="px-4 pt-4 text-xs text-codialis-gray md:px-6">
          Ces éléments entrent dans le calcul, mais ne se règlent pas : un filtre écarte le
          couple candidat / besoin, un signal de contexte informe sans jamais peser sur le
          classement.
        </p>
        <div className="divide-y divide-gray-100">
          {data.horsScore.map((element) => (
            <HorsScore key={element.code} element={element} />
          ))}
        </div>
      </Carte>

      <Carte titre="Déclenchement" sansBordure>
        <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2 md:p-6">
          <Reglage
            libelle="Seuil de notification"
            aide="Score à partir duquel le pôle commercial est prévenu automatiquement."
          >
            <input
              type="number"
              min={0}
              max={100}
              value={brouillon.seuilNotification}
              onChange={(evenement) =>
                changerReglage('seuilNotification', Number(evenement.target.value))
              }
              className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-white px-3 text-sm"
            />
          </Reglage>

          <Reglage
            libelle="Seuil de proposition"
            aide="Score en deçà duquel une proposition n’est pas conservée."
          >
            <input
              type="number"
              min={0}
              max={100}
              value={brouillon.seuilProposition}
              onChange={(evenement) =>
                changerReglage('seuilProposition', Number(evenement.target.value))
              }
              className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-white px-3 text-sm"
            />
          </Reglage>

          <Reglage
            libelle="Propositions par candidat"
            aide="Nombre de meilleures entreprises retenues à chaque passage."
          >
            <input
              type="number"
              min={1}
              max={10}
              value={brouillon.limitePropositions}
              onChange={(evenement) =>
                changerReglage('limitePropositions', Number(evenement.target.value))
              }
              className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-white px-3 text-sm"
            />
          </Reglage>

          <Reglage
            libelle="Passage automatique"
            aide="Calcul quotidien à 6 h, heure de La Réunion. Le calcul à la demande reste possible."
            commeBloc
          >
            <button
              type="button"
              onClick={() => changerReglage('batchActif', !brouillon.batchActif)}
              aria-pressed={brouillon.batchActif}
              className={`min-h-[44px] w-full rounded-xl px-4 text-sm font-semibold transition ${
                brouillon.batchActif
                  ? 'bg-codialis-mint text-codialis-navy'
                  : 'border border-gray-200 bg-white text-codialis-gray'
              }`}
            >
              {brouillon.batchActif ? 'Actif' : 'Désactivé'}
            </button>
          </Reglage>

          {!seuilsCoherents && (
            <div className="md:col-span-2 min-w-0">
              <MessageErreur message="Le seuil de notification doit être au moins égal au seuil de proposition : sinon aucune proposition conservée ne serait notifiée." />
            </div>
          )}
        </div>

        {data.modifieLe && (
          <p className="px-4 pb-4 text-[11px] text-codialis-gray md:px-6">
            Dernière modification le {horodatage(data.modifieLe)}
            {data.modifiePar ? ` par ${data.modifiePar}` : ''}. Le barème ne recalcule pas les
            propositions déjà tranchées : il s’applique au prochain passage.
          </p>
        )}
      </Carte>
    </div>
  );
}

function ReglageCritere({
  critere,
  poids,
  actif,
  onPoids,
  onBasculer,
}: {
  critere: LigneCritere;
  poids: number;
  actif: boolean;
  onPoids: (poids: number) => void;
  onBasculer: () => void;
}): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-[1fr_auto] md:items-center md:p-6">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-codialis-navy">{critere.libelle}</span>
          {!actif && <Pastille texte="Neutralisé" ton="neutral" />}
        </div>
        <p className="mt-0.5 text-xs text-codialis-gray">{critere.description}</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {/* Curseur pour le geste rapide au doigt, champ chiffré pour la valeur
            exacte : au doigt seul, viser 15 plutôt que 16 est illusoire. */}
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={poids}
          disabled={!actif}
          onChange={(evenement) => onPoids(Number(evenement.target.value))}
          aria-label={`Poids du critère ${critere.libelle}`}
          className="h-11 w-40 accent-codialis-mint disabled:opacity-40 sm:w-48"
        />
        <input
          type="number"
          min={0}
          max={100}
          value={poids}
          disabled={!actif}
          onChange={(evenement) => onPoids(Number(evenement.target.value))}
          aria-label={`Poids exact du critère ${critere.libelle}`}
          className="min-h-[44px] w-20 rounded-xl border border-gray-200 bg-white px-3 text-sm disabled:opacity-40"
        />
        <button
          type="button"
          onClick={onBasculer}
          aria-pressed={actif}
          className="min-h-[44px] rounded-xl border border-gray-200 bg-white px-3 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
        >
          {actif ? 'Neutraliser' : 'Réactiver'}
        </button>
      </div>
    </div>
  );
}

function HorsScore({ element }: { element: CritereHorsScore }): JSX.Element {
  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-codialis-navy">{element.libelle}</span>
        <Pastille
          texte={element.role === 'FILTRE' ? 'Filtre éliminatoire' : 'Contexte'}
          ton={element.role === 'FILTRE' ? 'warning' : 'neutral'}
        />
      </div>
      <p className="mt-0.5 text-xs text-codialis-gray">{element.explication}</p>
    </div>
  );
}

/**
 * Un réglage et son aide. `commeBloc` pour un contenu qui n'est pas un champ :
 * un <label> transmet son clic au premier élément étiquetable, et lire l'aide
 * d'un interrupteur ne doit pas le basculer.
 */
function Reglage({
  libelle,
  aide,
  children,
  commeBloc = false,
}: {
  libelle: string;
  aide: string;
  children: JSX.Element;
  commeBloc?: boolean;
}): JSX.Element {
  const Enveloppe = commeBloc ? 'div' : 'label';
  return (
    <Enveloppe className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
        {libelle}
      </span>
      {children}
      <span className="mt-1 block text-[11px] text-codialis-gray">{aide}</span>
    </Enveloppe>
  );
}
